import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import RowElement from "components/RowElement";
import { FORM_ERROR } from "final-form";
import useBoolean from "hooks/useBoolean";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useIntl } from "react-intl";
import {
  SaveSettingValueRequest,
  SettingCache,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import SettingValueField from "./SettingValueField";
import SettingValuePreview from "./SettingValuePreview";
import { validateSettingValue, prepareSettingValue } from "./settingValueUtils";

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  rowTitle: {
    float: "left",
    fontWeight: 600,
  },
};


type SettingValueFormProps = {
  setting: SettingCache;
  readonly?: boolean;
  onSubmit: (values: SaveSettingValueRequest) => Promise<any>;
  onDelete: () => Promise<any>;
};

const SettingValueForm = ({
  setting,
  readonly = true,
  onSubmit,
  onDelete,
}: SettingValueFormProps) => {
  const { formatMessage: t } = useIntl();
  const [confirmOpen, confirmOpenActions] = useBoolean(false);
  const [confirmDeleteOpen, confirmDeleteOpenActions] = useBoolean(false);
  const [formValues, setFormValues] = useState<SaveSettingValueRequest | null>(
    null
  );

  const detailsRows = [
    {
      title: t({ id: "valueLabel" }),
      component: <SettingValuePreview value={setting} />,
    },
    RowElement(
      "settingValueInheritedFromLabel",
      setting?.inheritedOrganizationName!
    ),
  ].filter((r) => !!r.component);

  const init = useMemo(
    () => ({ value: setting?.value ?? "" }),
    [setting?.value]
  );

  const validateForm = (values: SaveSettingValueRequest) => {
    const errors = {} as any;
    if (values.value === init.value) {
      errors.value = t({ id: "noChangesLabel" });
    }
    const typeError = validateSettingValue(setting, values, t);
    if (typeError) {
      errors.value = typeError;
    }
    return errors;
  };

  const handleSubmit = async (values: SaveSettingValueRequest) => {
    confirmOpenActions.setTrue();
    setFormValues(values);
  };

  const onConfirm = async () => {
    try {
      return await onSubmit(formValues!);
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    } finally {
      setFormValues(null);
      confirmOpenActions.setFalse();
    }
  };

  const handleDelete = async () => {
    confirmDeleteOpenActions.setTrue();
  };

  const onDeleteConfirm = async () => {
    try {
      return await onDelete();
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    } finally {
      setFormValues(null);
      confirmDeleteOpenActions.setFalse();
    }
  };

  return (
    <>
      {readonly && (
        <TableContainer component={Paper}>
          <Table>
            <TableBody>
              {detailsRows.map(({ title, component }, i) => (
                <TableRow key={i}>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ width: "20vw" }}
                  >
                    <Box component="span" sx={styles.rowTitle}>
                      {title}
                    </Box>
                  </TableCell>
                  <TableCell>{component}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!readonly && (
        <Form
          onSubmit={async (values) => {
            const transformed = SaveSettingValueRequest.fromJS(values);
            transformed.id = setting?.settingValueId;
            transformed.settingId = setting?.settingId!;

            transformed.value = prepareSettingValue(transformed.value, setting.settingTypeId);

            return await handleSubmit(transformed);
          }}
          initialValues={init}
          validate={validateForm}
          render={({ handleSubmit, submitting, submitError, values }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent dividers>
                <Box>
                  <SettingValueField
                    settingTypeId={setting?.settingTypeId}
                    settingOptions={setting?.settingOptions}
                  />
                  {submitError && (
                    <Alert sx={styles.feedback} severity="error">
                      {submitError}
                    </Alert>
                  )}
                </Box>
              </DialogContent>

              <DialogActions>
                {!setting?.ancestorSettingValueId &&
                  !setting?.isOrganizationRoot && (
                    <Button
                      disabled={submitting}
                      onClick={handleDelete}
                    >
                      {t({ id: "deleteSettingValueButton" })}
                    </Button>
                  )}

                <Button
                  type="submit"
                  color="primary"
                  disabled={submitting || values.value === init.value}
                >
                  {t({ id: "saveButton" })}
                </Button>
              </DialogActions>

              <ConfirmDialog
                dialogTitleKey="areYouSure"
                rawContent={t(
                  { id: "settingSaveConfirmationText" },
                  { organization: setting?.organizationName! }
                )}
                open={confirmOpen}
                onCancel={confirmOpenActions.setFalse}
                handleConfirm={onConfirm}
              />

              <ConfirmDialog
                dialogTitleKey="areYouSure"
                rawContent={t(
                  { id: "settingDeleteConfirmationText" },
                  { organization: setting?.organizationName! }
                )}
                open={confirmDeleteOpen}
                onCancel={confirmDeleteOpenActions.setFalse}
                handleConfirm={onDeleteConfirm}
              />
            </form>
          )}
        />
      )}
    </>
  );
};

export default SettingValueForm;
